<template>
  <div
    class="box flex h-full flex-col overflow-hidden rounded-md shadow-xl sm:mx-5"
  >
    <PdfViewer
      v-if="isLoaded"
      :url="pdfUrl"
      :title="document.name"
      :download-title="downloadTitle"
    >
      <template #toolbar-left>
        <BaseButton
          :title="$t('button.back')"
          icon="long-arrow-left"
          inverse-icon-order
          color="warning"
          size="small"
          @click="$router.push({ name: 'work_form_documents_route' })"
        />
      </template>
      <template #option-pane="{ loaded, download, print }">
        <div class="flex flex-1 flex-col gap-4">
          <template v-if="document.with_form && document.form">
            <h3 class="text-xl font-bold capitalize-first">
              {{ $t('global.form') }}
            </h3>
            <WorkDocumentForm :document="document" />
          </template>
          <div class="mt-auto flex flex-col gap-2 border-t pt-3">
            <BaseButton
              :title="$t('button.download')"
              icon="download"
              color="primary"
              :disabled="!loaded"
              @click="download"
            />
            <BaseButton
              :title="$t('button.print')"
              icon="print"
              color="primary"
              :disabled="!loaded"
              @click="print"
            />
            <BaseButton
              :title="$t('button.attach_to_work')"
              icon="paperclip"
              color="dark"
              @click="saveAsAttachment"
            />
          </div>
        </div>
      </template>
    </PdfViewer>
    <div v-else class="flex flex-1 items-center justify-center">
      <TheLoadingComponent />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import PdfViewer from '@c/PdfViewer/PdfViewer.vue'
import TheLoadingComponent from '@c/TheLoadingComponent.vue'
import WorkDocumentForm from './components/WorkDocumentForm.vue'

/**
 * A work's document rendered as PDF, with its form (if any) on the right: the
 * PDF follows the answers, a moment after the last change.
 */
export default {
  name: 'WorkDocumentRenderPage',
  components: { PdfViewer, TheLoadingComponent, WorkDocumentForm },
  props: {
    id: { type: String, required: true },
    document_id: { type: String, required: true }
  },
  data() {
    return { pdfUrl: null, debounceTimer: null }
  },
  computed: {
    ...mapGetters({
      work: 'work/getWork',
      auth: 'auth/getAuth',
      document: 'document/getDocument'
    }),
    isLoaded() {
      return this.document?.id?.toString() === this.document_id
    },
    query() {
      const params = new URLSearchParams()
      if (this.document?.with_form) {
        this.document.form?.forEach((question) => {
          params.append(question.name, question.value ?? '')
        })
      }
      params.append('lang', this.auth.language)
      return params.toString()
    },
    downloadTitle() {
      const { first_name, last_name } = this.work?.deceased ?? {}
      const deceased = [first_name, last_name].filter(Boolean).join(' ')
      const name = this.document?.name ?? 'document'
      return deceased ? `${deceased} - ${name}` : name
    }
  },
  watch: {
    query(value) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.setPdfUrl(value), 600)
    }
  },
  async mounted() {
    if (this.document?.id?.toString() !== this.document_id) {
      await this.fetchDocument({ document_id: this.document_id })
    }
    // The form's date pickers emit their defaults once mounted: render
    // with them, not before.
    await this.$nextTick()
    clearTimeout(this.debounceTimer)
    this.setPdfUrl(this.query)
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer)
  },
  methods: {
    ...mapActions({
      fetchDocument: 'document/fetchDocument',
      saveDocumentAsAttachment: 'document/saveDocumentAsAttachment',
      fetchWork: 'work/fetchWorkByID'
    }),
    setPdfUrl(query) {
      this.pdfUrl = `api/render/document/${this.id}/${this.document_id}?${query}`
    },
    saveAsAttachment() {
      this.saveDocumentAsAttachment({
        url: `save/document/${this.id}/${this.document_id}?${this.query}`
      }).then(() => {
        this.$h.toastSuccess(this.$t('toasts.save_document_as_attachment'))
        this.fetchWork(this.work.id)
      })
    }
  }
}
</script>
